export interface Article {
  id: string
  slug: string
  title: string
  excerpt: string
  category: string
  date: string
  author: string
  readTime: string
  content: string
  tags?: string[]
}

export interface NavigationItem {
  id: string
  title: string
  path: string
  children?: NavigationItem[]
  isActive?: boolean
}

export interface FeaturedCategory {
  name: string
  link: string
  description: string
  icon: string
}

// Sample help center data
export const helpData: { articles: Article[] } = {
  articles: [
    {
      id: '1',
      slug: 'getting-started-with-lavender-flow',
      title: 'Getting Started with Lavender Flow',
      excerpt: 'Learn the basics of Lavender Flow and how to create your first multimodal AI workflow.',
      category: 'Guides',
      date: 'May 1, 2024',
      author: 'Alex Johnson',
      readTime: '5 min read',
      tags: ['getting-started', 'workflows', 'basics'],
      content: `Learn the basics of Lavender Flow and how to create your first multimodal AI workflow.

## Introduction to Lavender Flow

Lavender Flow is a visual, node-based platform for building and deploying multimodal AI agent pipelines. It supports text, images, video, audio, and real-time streaming data as input and output. This article will guide you through the basics of creating your first workflow.

The platform aims to simplify the development process, allowing users to focus on the logic of their AI applications rather than the underlying infrastructure. You can connect various pre-built components or create your own custom nodes.

## Understanding Flows

Workflows in Lavender Flow consist of "Flows," which are function-like units with \`FlowStart()\` inputs and \`FlowOutput()\` outputs. This enables type detection (e.g., chatbot, event-driven) and allows for complex control via \`FlowDispatch(Id=flowId)\`.

* **FlowStart():** Defines the entry point and input parameters for a flow.
* **FlowOutput():** Specifies the exit point and the data returned by a flow.
* **FlowDispatch():** Enables one flow to trigger another, facilitating modular design.
* **Nodes:** Represent individual processing steps (e.g., API calls, data transformations, model inference).

\`\`\`python
# Example Python code block for a simple Flow
from lavender_flow import FlowStart, FlowOutput

@FlowStart()
def process_text_input(text: str):
  """Processes incoming text."""
  processed_text = text.upper() + " - PROCESSED"
  return FlowOutput(result=processed_text)

# In another flow:
# result_flow = FlowDispatch(Id='process_text_input', text="hello world")
# print(result_flow['result']) # Output: HELLO WORLD - PROCESSED
\`\`\`

## Creating Your First Workflow

To create your first workflow, navigate to the **Workflows** section in the web UI and click on "New Workflow." You'll be presented with a canvas where you can drag and drop nodes to build your pipeline visually.

> This visual approach makes building complex pipelines intuitive, even for users less familiar with coding intricate backend systems.

Key steps involve:

1. Selecting a trigger node (e.g., HTTP Request, Scheduled Timer).
2. Adding processing nodes (e.g., data transformation, AI model call).
3. Connecting nodes logically using the drag-and-drop interface.
4. Configuring parameters for each node.
5. Saving and testing the workflow.

## Deploying Your Workflow

Once you've created and tested your workflow, you can deploy it directly from the UI. Lavender Flow supports various deployment options, including generating standalone services, embedding within existing applications, or running as serverless functions.

| Feature | Description | Availability |
|---------|-------------|--------------|
| Visual Editor | Drag-and-drop interface for building workflows. | ✅ Included |
| Multimodal Support | Handles text, image, audio, video data. | ✅ Included |
| Real-time Collaboration | Multiple users editing the same workflow. | ✅ Included |
| Version Control | Track changes and revert to previous versions. | 🚧 Planned |`
    },
    {
      id: '2',
      slug: 'understanding-flows-and-workflows',
      title: 'Understanding Flows and Workflows',
      excerpt: 'Deep dive into the core concepts of Flows and how they work together to create powerful AI workflows.',
      category: 'Tutorials',
      date: 'May 5, 2024',
      author: 'Sarah Chen',
      readTime: '8 min read',
      tags: ['flows', 'workflows', 'concepts'],
      content: `Deep dive into the core concepts of Flows and how they work together to create powerful AI workflows.

## What are Flows?

Flows are the fundamental building blocks of Lavender Flow workflows. Think of them as reusable functions that can be connected together to create complex AI pipelines.

### Flow Structure

Every Flow has:
- **Inputs**: Data that flows into the function
- **Processing**: The actual work performed
- **Outputs**: Results sent to the next Flow

## Flow Types

### 1. Trigger Flows
These start your workflow execution:
- **HTTP Trigger**: Responds to web requests
- **Schedule Trigger**: Runs on a time schedule
- **Event Trigger**: Responds to external events

### 2. Processing Flows
These transform or analyze data:
- **AI Model Flows**: Run inference with various AI models
- **Data Transform Flows**: Clean, format, or restructure data
- **API Call Flows**: Interact with external services

### 3. Output Flows
These deliver results:
- **Response Flows**: Send HTTP responses
- **Notification Flows**: Send emails, messages, or alerts
- **Storage Flows**: Save data to databases or files

## Best Practices

1. **Keep Flows focused** - Each Flow should have a single responsibility
2. **Use meaningful names** - Clear naming helps with maintenance
3. **Handle errors gracefully** - Add error handling to prevent workflow failures
4. **Test individually** - Verify each Flow works before connecting them`
    },
    {
      id: '3',
      slug: 'lavender-flow-api-reference',
      title: 'Lavender Flow API Reference',
      excerpt: 'Complete API documentation for integrating with Lavender Flow programmatically.',
      category: 'API',
      date: 'April 28, 2024',
      author: 'Mike Rodriguez',
      readTime: '12 min read',
      tags: ['api', 'reference', 'integration'],
      content: `Complete API documentation for integrating with Lavender Flow programmatically.

## Authentication

All API requests require authentication using a bearer token:

\`\`\`bash
curl -H "Authorization: Bearer YOUR_API_TOKEN" \\
  https://api.lavenderflow.com/v1/workflows
\`\`\`

## Workflows API

### List Workflows

\`GET /v1/workflows\`

Returns a list of all workflows in your account.

**Response:**
\`\`\`json
{
  "workflows": [
    {
      "id": "wf_123456",
      "name": "My First Workflow",
      "status": "active",
      "created_at": "2024-05-01T10:00:00Z"
    }
  ]
}
\`\`\`

### Execute Workflow

\`POST /v1/workflows/{workflow_id}/execute\`

Triggers a workflow execution with input data.

**Request Body:**
\`\`\`json
{
  "input": {
    "text": "Hello world",
    "parameters": {
      "model": "gpt-4"
    }
  }
}
\`\`\`

**Response:**
\`\`\`json
{
  "execution_id": "exe_789012",
  "status": "running",
  "started_at": "2024-05-01T10:15:00Z"
}
\`\`\`

## Rate Limits

- **Free Plan**: 100 requests per hour
- **Pro Plan**: 1,000 requests per hour  
- **Teams Plan**: 10,000 requests per hour

## Error Handling

All errors return a JSON response with error details:

\`\`\`json
{
  "error": {
    "code": "WORKFLOW_NOT_FOUND",
    "message": "The specified workflow does not exist",
    "details": {}
  }
}
\`\`\``
    },
    {
      id: '4',
      slug: 'billing-and-usage',
      title: 'Billing and Usage Guide',
      excerpt: 'Understanding your Lavender Flow billing, usage limits, and how to manage your subscription.',
      category: 'Billing',
      date: 'May 10, 2024',
      author: 'Emma Wilson',
      readTime: '6 min read',
      tags: ['billing', 'subscription', 'usage'],
      content: `Understanding your Lavender Flow billing, usage limits, and how to manage your subscription.

## Subscription Plans

### Free Plan
- Up to 10 workflows
- 1,000 executions per month
- Community support
- $5 in signup credits

### Pro Plan - $29/month
- Unlimited workflows
- 50,000 executions per month
- Email support
- $10 in signup credits

### Teams Plan - $99/month (3 users)
- Everything in Pro
- Team collaboration features
- Priority support
- $20 in signup credits

### Teams+ Plan - $50/month per user
- Everything in Teams
- Advanced analytics
- Custom integrations
- Dedicated support

## Usage-Based Costs

Beyond your plan's included executions, you'll be charged for:

- **CPU/GPU Runtime**: $0.001 per compute second
- **API Calls**: Varies by provider (OpenAI, etc.)
- **Storage**: $0.10 per GB per month

## Managing Your Billing

### View Usage
1. Go to **Settings** > **Billing**
2. View current usage and remaining quota
3. Download detailed usage reports

### Upgrade/Downgrade
1. Visit the **Billing** section
2. Select your desired plan
3. Changes take effect immediately

### Payment Methods
We accept all major credit cards and PayPal. Enterprise customers can request invoicing.

## Annual Discounts

Save 20% by switching to annual billing:
- **Pro Annual**: $278.40/year (save $70.80)
- **Teams Annual**: $950.40/year (save $237.60)`
    },
    {
      id: '5',
      slug: 'multimodal-ai-workflows',
      title: 'Building Multimodal AI Workflows',
      excerpt: 'Learn how to create workflows that process text, images, audio, and video together.',
      category: 'Tutorials',
      date: 'May 15, 2024',
      author: 'Dr. Lisa Park',
      readTime: '15 min read',
      tags: ['multimodal', 'ai', 'workflows', 'advanced'],
      content: `Learn how to create workflows that process text, images, audio, and video together.

## What is Multimodal AI?

Multimodal AI refers to systems that can understand and process multiple types of data simultaneously - text, images, audio, video, and more. Lavender Flow makes it easy to build these sophisticated pipelines.

## Common Multimodal Patterns

### 1. Content Analysis Pipeline
Process social media posts that contain both text and images:

1. **Text Analysis Flow**: Extract sentiment from post text
2. **Image Recognition Flow**: Identify objects in attached images  
3. **Fusion Flow**: Combine insights from both modalities
4. **Decision Flow**: Take action based on combined analysis

### 2. Customer Support Bot
Handle support tickets with attachments:

1. **Text Understanding**: Parse the customer's question
2. **Image Analysis**: Analyze screenshots or product photos
3. **Context Fusion**: Combine text and visual context
4. **Response Generation**: Create a comprehensive response

## Best Practices for Multimodal Workflows

### Data Synchronization
Ensure related data (text + image from same source) flows through your pipeline together:

\`\`\`python
# Example: Keeping text and image paired
@FlowStart()
def process_social_post(post_data):
    return {
        'text': post_data['content'],
        'image_url': post_data['image_url'],
        'post_id': post_data['id']  # Keep reference
    }
\`\`\`

### Error Handling
Different modalities may fail at different rates. Build robust error handling:

- **Graceful Degradation**: If image processing fails, continue with text-only analysis
- **Retry Logic**: Temporary failures are common with AI services
- **Fallback Options**: Have backup models/services available

### Performance Optimization
Multimodal processing can be expensive:

- **Parallel Processing**: Process different modalities simultaneously
- **Caching**: Cache expensive AI model results
- **Smart Routing**: Only process modalities that are needed

## Example: Video Content Moderator

Let's build a complete video content moderation workflow:

### Step 1: Video Processing
- Extract audio track for speech analysis
- Extract keyframes for visual analysis
- Extract metadata (duration, resolution, etc.)

### Step 2: Parallel Analysis
- **Audio Analysis**: Transcribe speech, analyze tone
- **Visual Analysis**: Detect inappropriate content, identify objects
- **Metadata Check**: Verify technical compliance

### Step 3: Decision Making
Combine all analysis results to make moderation decision:

\`\`\`python
@FlowStart()
def make_moderation_decision(audio_score, visual_score, metadata_check):
    if audio_score < 0.3 or visual_score < 0.3:
        return {'action': 'block', 'reason': 'inappropriate_content'}
    elif not metadata_check['compliant']:
        return {'action': 'block', 'reason': 'technical_violation'}
    else:
        return {'action': 'approve', 'confidence': min(audio_score, visual_score)}
\`\`\`

This approach gives you much more accurate results than analyzing any single modality alone.`
    },
    {
      id: '6',
      slug: 'troubleshooting-common-issues',
      title: 'Troubleshooting Common Issues',
      excerpt: 'Solutions to the most frequently encountered problems when building workflows.',
      category: 'Guides',
      date: 'May 20, 2024',
      author: 'Alex Johnson',
      readTime: '10 min read',
      tags: ['troubleshooting', 'debugging', 'help'],
      content: `Solutions to the most frequently encountered problems when building workflows.

## Workflow Execution Issues

### My workflow isn't triggering
**Possible causes:**
- Trigger configuration is incorrect
- Webhook URL is wrong or not accessible
- Schedule trigger is in the wrong timezone

**Solutions:**
1. Check trigger settings in the workflow editor
2. Test triggers manually using the "Test" button
3. Verify webhook URLs are publicly accessible
4. Ensure scheduled triggers use UTC timezone

### Workflow runs but produces no output
**Common causes:**
- Missing or incorrect output configuration
- Data flow interrupted between nodes
- Filter conditions blocking all data

**Debug steps:**
1. Enable debug mode to see intermediate outputs
2. Check each node's output in the execution logs
3. Verify filter conditions aren't too restrictive

## Node Connection Problems

### "Invalid connection" error
This happens when you try to connect incompatible node types.

**Solution:**
- Check that output data types match input requirements
- Use transform nodes to convert data formats if needed
- Refer to node documentation for supported data types

### Data not flowing between nodes
**Check these items:**
- Connections are properly established (solid lines, not dashed)
- Previous node completed successfully
- Data format matches expected input schema

## Performance Issues

### Workflow running slowly
**Optimization strategies:**
- Use parallel processing where possible
- Cache expensive operations
- Optimize AI model selection (smaller models for simple tasks)
- Add timeouts to prevent hanging operations

### API rate limit errors
**Solutions:**
- Implement exponential backoff retry logic
- Use multiple API keys to increase rate limits
- Cache API responses when appropriate
- Consider upgrading to higher-tier API plans

## Common Error Messages

### "Authentication failed"
- Verify API keys are correct and not expired
- Check that API keys have necessary permissions
- Ensure credentials are stored securely in environment variables

### "Insufficient credits"
- Check your account balance in the billing section
- Add payment method or add more credits
- Consider upgrading to a higher plan

### "Timeout error"
- Increase timeout settings in node configuration
- Optimize workflow to reduce processing time
- Break large tasks into smaller chunks

## Getting Help

If you're still experiencing issues:

1. **Check the logs** - Detailed execution logs are available in the workflow editor
2. **Search this help center** - Many solutions are already documented
3. **Contact support** - Use the chat widget or email support@lavenderflow.com
4. **Community forum** - Ask questions and get help from other users

## Preventive Measures

### Testing Strategies
- Test workflows with small datasets first
- Use the workflow debugger to step through execution
- Set up monitoring alerts for production workflows

### Best Practices
- Always handle errors gracefully
- Set appropriate timeouts for all operations
- Monitor workflow performance regularly
- Keep backups of working workflow configurations`
    }
  ]
}

// Helper functions
export const getArticleBySlug = (slug: string): Article | undefined => {
  return helpData.articles.find(article => article.slug === slug)
}

export const getArticlesByCategory = (category: string): Article[] => {
  return helpData.articles.filter(article => article.category.toLowerCase() === category.toLowerCase())
}

export const searchArticles = (query: string): Article[] => {
  const searchTerm = query.toLowerCase()
  return helpData.articles.filter(article =>
    article.title.toLowerCase().includes(searchTerm) ||
    article.excerpt.toLowerCase().includes(searchTerm) ||
    article.content.toLowerCase().includes(searchTerm) ||
    (article.tags && article.tags.some(tag => tag.toLowerCase().includes(searchTerm)))
  )
}

export const getAllCategories = (): string[] => {
  const categories = helpData.articles.map(article => article.category)
  return [...new Set(categories)]
}

export const getRelatedArticles = (currentArticleId: string, count: number = 3): Article[] => {
  const currentArticle = helpData.articles.find(article => article.id === currentArticleId)
  if (!currentArticle) return []

  // Get articles from same category, excluding current article
  const related = helpData.articles
    .filter(article =>
      article.id !== currentArticleId &&
      article.category === currentArticle.category
    )
    .slice(0, count)

  // If not enough related articles, fill with other articles
  if (related.length < count) {
    const additional = helpData.articles
      .filter(article =>
        article.id !== currentArticleId &&
        !related.includes(article)
      )
      .slice(0, count - related.length)

    related.push(...additional)
  }

  return related
}

export const getPopularArticles = (count: number = 6): Article[] => {
  // For now, return first few articles - in real app this would be based on view counts
  return helpData.articles.slice(0, count)
}

export const getRecentArticles = (count: number = 6): Article[] => {
  // Sort by date and return most recent
  return helpData.articles
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, count)
}

export interface HelpTopic {
  id: string
  title: string
  description: string
  link: string
}

export interface InteractiveFeaturedCategory {
  name: string
  description: string
  icon: string
  topics?: HelpTopic[]
  link?: string
}

export const featuredCategories: InteractiveFeaturedCategory[] = [
  {
    name: 'Getting Started',
    description: 'Learn the basics and set up your first workflow.',
    icon: 'book',
    topics: [
      {
        id: 'first-workflow',
        title: 'How do I create my first workflow?',
        description: 'Step-by-step guide to building your first workflow',
        link: '/help/articles/getting-started-with-lavender-flow#first-workflow'
      },
      {
        id: 'workflow-editor',
        title: 'Where is the workflow editor?',
        description: 'Finding and navigating the visual workflow builder',
        link: '/help/articles/getting-started-with-lavender-flow#workflow-editor'
      },
      {
        id: 'test-workflow',
        title: 'How do I test my workflow before deploying?',
        description: 'Testing and debugging workflows safely',
        link: '/help/articles/getting-started-with-lavender-flow#testing'
      },
      {
        id: 'flows-vs-workflows',
        title: "What's the difference between Flows and Workflows?",
        description: 'Understanding core concepts and terminology',
        link: '/help/articles/understanding-flows-and-workflows'
      },
      {
        id: 'connect-apis',
        title: 'How do I connect to external APIs?',
        description: 'Integrating with third-party services and APIs',
        link: '/help/articles/getting-started-with-lavender-flow#api-connections'
      },
      {
        id: 'example-templates',
        title: 'Where do I find example templates?',
        description: 'Browse pre-built workflow templates to get started',
        link: '/gallery/workflows'
      }
    ]
  },
  {
    name: 'Billing & Account',
    description: 'Manage your subscription and usage details.',
    icon: 'settings',
    topics: [
      {
        id: 'cancel-subscription',
        title: 'How do I cancel my subscription?',
        description: 'Step-by-step guide to canceling your plan',
        link: '/help/articles/billing-and-usage#canceling-subscription'
      },
      {
        id: 'refund-credits',
        title: "I'd like to refund a credit purchase",
        description: 'Information about credit refund policies',
        link: '/help/articles/billing-and-usage#credit-refunds'
      },
      {
        id: 'usage-calculation',
        title: 'How is my usage calculated?',
        description: 'Understanding your usage metrics and billing',
        link: '/help/articles/billing-and-usage#usage-calculation'
      },
      {
        id: 'upgrade-plan',
        title: 'How do I upgrade my plan?',
        description: 'Upgrading to Pro or Teams plans',
        link: '/help/articles/billing-and-usage#upgrading-plans'
      },
      {
        id: 'payment-methods',
        title: 'What payment methods do you accept?',
        description: 'Accepted payment options and billing cycles',
        link: '/help/articles/billing-and-usage#payment-methods'
      },
      {
        id: 'billing-history',
        title: 'Where can I view my billing history?',
        description: 'Accessing invoices and payment records',
        link: '/help/articles/billing-and-usage#billing-history'
      }
    ]
  },
  {
    name: 'Teams',
    description: 'Collaborate with your team on workflows.',
    icon: 'users',
    topics: [
      {
        id: 'invite-team-members',
        title: 'How do I invite team members?',
        description: 'Adding users to your team workspace',
        link: '/help/articles/collaboration-features#inviting-users'
      },
      {
        id: 'team-permissions',
        title: 'How do team permissions work?',
        description: 'Understanding roles and access levels',
        link: '/help/articles/collaboration-features#permissions'
      },
      {
        id: 'share-workflows',
        title: 'How do I share workflows with my team?',
        description: 'Collaborative workflow editing and sharing',
        link: '/help/articles/collaboration-features#sharing-workflows'
      },
      {
        id: 'real-time-editing',
        title: 'Can multiple people edit a workflow at once?',
        description: 'Real-time collaboration features',
        link: '/help/articles/collaboration-features#real-time-editing'
      },
      {
        id: 'team-billing',
        title: 'How does billing work for teams?',
        description: 'Team plan billing and user management',
        link: '/help/articles/billing-and-usage#team-billing'
      },
      {
        id: 'remove-team-member',
        title: 'How do I remove a team member?',
        description: 'Managing team members and access',
        link: '/help/articles/collaboration-features#remove-users'
      }
    ]
  },
  {
    name: 'Tutorials & Guides',
    description: 'Step-by-step instructions for common tasks.',
    icon: 'layers',
    link: '/help/articles?category=tutorials'
  }
]

// Documentation sidebar navigation
export const docsNavigation: NavigationItem[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    path: '/help',
    children: [
      {
        id: 'introduction',
        title: 'Introduction',
        path: '/help/articles/getting-started-with-lavender-flow'
      },
      {
        id: 'first-workflow',
        title: 'Your First Workflow',
        path: '/help/articles/understanding-flows-and-workflows'
      }
    ]
  },
  {
    id: 'tutorials',
    title: 'Tutorials',
    path: '/help/tutorials',
    children: [
      {
        id: 'multimodal',
        title: 'Multimodal Workflows',
        path: '/help/articles/multimodal-ai-workflows'
      },
      {
        id: 'troubleshooting',
        title: 'Troubleshooting',
        path: '/help/articles/troubleshooting-common-issues'
      }
    ]
  },
  {
    id: 'api',
    title: 'API Reference',
    path: '/help/api',
    children: [
      {
        id: 'api-reference',
        title: 'API Documentation',
        path: '/help/articles/lavender-flow-api-reference'
      }
    ]
  },
  {
    id: 'billing',
    title: 'Billing',
    path: '/help/billing',
    children: [
      {
        id: 'billing-guide',
        title: 'Billing Guide',
        path: '/help/articles/billing-and-usage'
      }
    ]
  }
]
